import { Select } from 'antd';
import React, { useEffect, useMemo } from 'react';

import { useTranslate } from '@/hooks/useTranslate';
import PaymentStore, {
  CurrencyType,
  filterCommoditiesByCurrency,
  getAvailableCurrencies,
  getPaymentMethodsByCurrency,
  PaymentMethod,
} from '@/store/Payment';

import styles from './style.module.scss';

interface CurrencySelectorProps {
  // 当前商品数据数组
  commodities: any[];
  // 是否禁用
  disabled?: boolean;
  // 选择变化回调
  onChange?: (currency: CurrencyType, method: PaymentMethod) => void;
}

const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  commodities,
  disabled = false,
  onChange,
}) => {
  const { t } = useTranslate();
  const { selectedCurrency, paymentMethod, setSelectedCurrency } = PaymentStore();

  // 计算可用的货币类型
  const availableCurrencies = useMemo(() => {
    return getAvailableCurrencies(commodities);
  }, [commodities]);

  // 计算支付方式选项
  const paymentOptions = useMemo(() => {
    return getPaymentMethodsByCurrency(availableCurrencies);
  }, [availableCurrencies]);

  // 是否只有单一支付方式
  const isSingleOption = paymentOptions.length <= 1;

  // 初始化默认支付方式
  useEffect(() => {
    if (paymentOptions.length > 0) {
      // 如果当前选中的货币不在可用列表中，则选择第一个
      const currentValid = paymentOptions.find(
        (opt) => opt.currency === selectedCurrency,
      );
      if (!currentValid) {
        const defaultOption = paymentOptions[0];
        setSelectedCurrency(defaultOption.currency);
        onChange?.(defaultOption.currency, defaultOption.value);
      }
    }
  }, [paymentOptions]);

  // 处理选择变化
  const handleChange = (value: PaymentMethod) => {
    const option = paymentOptions.find((opt) => opt.value === value);
    if (option) {
      setSelectedCurrency(option.currency);
      onChange?.(option.currency, value);
    }
  };

  // 渲染选项标签
  const renderContent = (method?: PaymentMethod, isPopup?: boolean) => {
    if (!method) return null;
    const isStripeAlipay = method === 'stripeAlipay';
    const textColor = isPopup ? 'text-app-secondary' : 'text-app-primary';
    return (
      <div className="flex items-center gap-[8px]">
        {isStripeAlipay ? (
          <>
            <img src="/images/StripeAlipay.svg" alt="" />
            <span className={`text-[12px] ${textColor}`}>{t('Alipay')}(CNY)</span>
          </>
        ) : (
          <>
            <img src="/images/StripeNative.svg" alt="" />
            <span className={`text-[12px] ${textColor}`}>Stripe(USD)</span>
          </>
        )}
        {paymentMethod === method && isPopup && (
          <img src="/images/StripeYes.svg" alt="" />
        )}
      </div>
    );
  };

  const renderLabel = (props: { value: string | number; label?: React.ReactNode }) => {
    const selected = paymentOptions.find((opt) => opt.value === props.value);
    return renderContent(selected?.value);
  };

  // 如果没有可用的支付方式或只有一个，可以隐藏或显示为只读
  if (paymentOptions.length === 0) {
    return null;
  }

  return (
    <div className="flex justify-end items-center gap-[12px] px-[60px] pb-[8px] pr-[40px]">
      <span className="text-[14px] text-[#fff]">{t('Payment-Method')}</span>
      <Select
        classNames={{
          root: styles['currencySelector'],
          popup: {
            root: styles['currencySelector-popup'],
          },
        }}
        value={paymentMethod}
        disabled={disabled || isSingleOption}
        onChange={handleChange}
        style={{ width: 160 }}
        options={paymentOptions.map((opt) => ({
          value: opt.value,
          label: opt.label,
        }))}
        labelRender={renderLabel}
        optionRender={(option) => {
          const opt = paymentOptions.find((o) => o.value === option.value);
          if (!opt) return null;
          return renderContent(opt.value, true);
        }}
      />
    </div>
  );
};

export default CurrencySelector;

/**
 * 获取当前货币对应的商品
 * 用于在选择支付方式后过滤显示对应货币的商品
 */
export const useCurrencyFilteredCommodities = (commodities: any[]) => {
  const { selectedCurrency } = PaymentStore();

  return useMemo(() => {
    return filterCommoditiesByCurrency(commodities, selectedCurrency);
  }, [commodities, selectedCurrency]);
};
